import type { AnimatedSprite, AnimatedSpriteFrames, DestroyOptions } from 'pixi.js';
import { Assets, Container } from 'pixi.js';
import type { BlockerView } from '../../types';
import { TapeBlockerLayout } from '../../../../../layouts/blockers/TapeBlockerLayout';
import type { TapeBlockerModel } from '../../models/blockers/TapeBlockerModel';
import { TILE_Z_OFFSET } from '../board/TileView';
import { soundManager } from '../../../../app/SoundManager';
import type { TileStackView } from '../board/TileStackView';
import { FXParticleLayer } from '../../../../libs/particles/FXParticleLayer';
import RemoveParticlesJSON from '../../../../../assets/particles{ignore}/Tape_Blocker_Remove_Particles.json';
import type { FXParticleEmitterConfig } from '../../../../libs/particles/FXParticleEmitterConfig';
import { getRandomElement } from '../../../../utils/array-utils';
import { repeatUntilFalse } from '../../../../utils/async-utils';

interface TapeBlockerLayer {
  top: AnimatedSprite;
  side: Container;
  getSideHeight: (stackHeight: number) => number;
  frames: string[];
}

const diagonalTapeFrames = [
  'Animation_Diagonal_Tape_1',
  'Animation_Diagonal_Tape_2',
  'Animation_Diagonal_Tape_3',
  'Animation_Diagonal_Tape_4',
];

const verticalTapeFrames = [
  'Animation_Vertical_Tape_1',
  'Animation_Vertical_Tape_2',
  'Animation_Vertical_Tape_3',
  'Animation_Vertical_Tape_4',
];

const particleEmitterSeeds = [23, 42, 99];

const disabledStackTint = { r: 215, g: 215, b: 215 };

export class TapeBlockerView extends Container implements BlockerView {
  private readonly layout: TapeBlockerLayout;

  private hp = 3;

  private stackTileHeight = 0;

  private readonly tapeLayers: TapeBlockerLayer[];

  private readonly particleLayer: FXParticleLayer;

  constructor(
    private readonly model: TapeBlockerModel,
    private readonly stackView: TileStackView,
  ) {
    super();

    this.layout = new TapeBlockerLayout();
    this.layout.root.props({
      parent: this,
    });

    this.tapeLayers = [
      {
        top: this.layout.piece_d2_1,
        side: this.layout.piece_d2_2,
        getSideHeight: (stackHeight: number): number => 17 + stackHeight,
        frames: diagonalTapeFrames,
      },
      {
        top: this.layout.piece_d1_1,
        side: this.layout.piece_d1_2,
        getSideHeight: (stackHeight: number): number => 18 + stackHeight,
        frames: diagonalTapeFrames,
      },
      {
        top: this.layout.piece_v_1,
        side: this.layout.piece_v_2,
        getSideHeight: (stackHeight: number): number => stackHeight - 2,
        frames: verticalTapeFrames,
      },
    ];

    this.layout.glow.props({
      alpha: 0,
    });

    this.particleLayer = new FXParticleLayer(true).props({
      parent: this.layout.root,
    });

    this.updateStackTileHeight(this.stackView.model.getTileCount());
    this.setHp(model.getHp());
  }

  public getModel(): TapeBlockerModel {
    return this.model;
  }

  public destroy(options?: DestroyOptions): void {
    if (this.destroyed) return;
    void repeatUntilFalse(() => this.particleLayer.hasActiveEmitters()).then(() => {
      this.particleLayer.destroy();
      super.destroy(options);
    });
  }

  public updateStackTileHeight(tiles: number): void {
    this.stackTileHeight = tiles;
    const stackHeight = TILE_Z_OFFSET * tiles;
    const sidesVisible = this.stackTileHeight > 0;

    this.tapeLayers.forEach((layer) => {
      layer.side.visible = sidesVisible;
      layer.side.height = layer.getSideHeight(stackHeight);
    });

    this.layout.glow.height += stackHeight;
  }

  public onModelChanged(): void {
    if (this.hp - 1 === this.model.getHp()) {
      void this.animateHit();
    } else {
      this.setHp(this.model.getHp());
    }
  }

  public async animateRemoval(): Promise<void> {
    await this.animateHit();
    this.destroy({ children: true });
  }

  private setHp(hp: number): void {
    if (hp < 1 || hp > 3) return;

    this.tapeLayers.forEach((layer, layerIndex) => {
      const layerVisible = layerIndex + 1 <= hp;
      layer.top.visible = layerVisible;
      layer.side.visible = layerVisible && this.stackTileHeight > 0;
    });

    if (this.hp > 0) this.stackView.tintTiles(disabledStackTint);
    else this.stackView.tintTiles(0xffffff);

    this.hp = hp;
  }

  private async animateHit(): Promise<void> {
    if (this.hp <= 0) return;
    this.hp -= 1;

    const { tapeLayers } = this;
    const tapeLayer = tapeLayers.at(this.hp);
    if (!tapeLayer) return;

    soundManager.play('Gameplay_Tape_Peel');

    await tapeLayer.side
      .tweenTo(
        {
          height: 0,
        },
        150,
        'quadOut',
        true,
      )
      .asyncOnComplete();

    if (this.hp === 0) {
      const emitterConfig = RemoveParticlesJSON as FXParticleEmitterConfig;
      const emitter = this.particleLayer.addParticleEmitter(0, 60, emitterConfig);
      const seeds = [getRandomElement(particleEmitterSeeds)];
      emitter.randomDataGenerator.sow(seeds);
    }

    await new Promise<void>((resolve): void => {
      tapeLayer.top.textures = tapeLayer.frames.map((frame) => Assets.get(frame)) as AnimatedSpriteFrames;
      tapeLayer.top.loop = false;
      tapeLayer.top.play();
      tapeLayer.top.animationSpeed = 0.4;
      tapeLayer.top.onComplete = resolve;
    });

    tapeLayer.top.tweenTo({ alpha: 0 }, 70, 'quadOut', true, 50);

    if (this.hp === 0) {
      const rgb = {
        ...disabledStackTint,
      };
      this.stackView.tweens
        .create(rgb)
        .to({ r: 255, g: 255, b: 255 }, 100, 'quadOut', true, 450)
        .onUpdate.add(() => {
          this.stackView.tintTiles(rgb);
        });

      await this.layout.glow
        .tweenTo(
          {
            alpha: 0.5,
          },
          450,
          'quadIn',
          true,
          0,
          0,
          true,
        )
        .asyncOnComplete();
    }
  }
}
